import { createContext, useContext, useEffect, type ReactNode } from 'react'

import type { ProjectPlacement } from './session'

/**
 * What a screen tells the shell about itself, and what it gets back.
 *
 * One thing goes up: the registered API route this screen is a view of. The
 * help drawer and the feedback report both key off it, so both are right on
 * every screen without either one guessing from the browser's URL — and the
 * documentation build checks its coverage against exactly that string.
 *
 * One thing comes down: which project is open, with its address and owner.
 */

export type ScreenValue = {
  placement: ProjectPlacement
  setHelpRoute: (route: string) => void
  setModuleCode: (code: string) => void
}

const ScreenContext = createContext<ScreenValue | null>(null)

export function ScreenProvider({
  value,
  children,
}: {
  value: ScreenValue
  children: ReactNode
}) {
  return <ScreenContext.Provider value={value}>{children}</ScreenContext.Provider>
}

export function useScreen(): ScreenValue {
  const value = useContext(ScreenContext)
  if (value === null) throw new Error('useScreen used outside the shell')
  return value
}

/**
 * Declare which API route this screen is a view of.
 *
 * Every screen calls this, including the generated ones. A screen that does
 * not is a screen whose `?` opens the previous screen's help, which is worse
 * than no help at all.
 */
export function useScreenRoute(apiRoute: string, moduleCode: string): ProjectPlacement {
  const { placement, setHelpRoute, setModuleCode } = useScreen()
  useEffect(() => {
    setHelpRoute(apiRoute)
    setModuleCode(moduleCode)
  }, [apiRoute, moduleCode, setHelpRoute, setModuleCode])
  return placement
}
